/**
 * Table of contents for `sections/ag-article.liquid`.
 *
 * The Claude Design "Astrography Text Page" mock lists its own sections by
 * hand. Articles come out of the Shopify rich-text editor instead, so the
 * list is built from the rendered body: every h2 gets an id (unless the
 * author already gave it one) and a numbered link.
 *
 * The element ships `hidden`; it only reveals itself once it knows the
 * article has at least `data-min` headings, so short posts stay clean and
 * nothing flashes in before the list exists.
 */
class AgToc extends HTMLElement {
  connectedCallback() {
    const body = document.querySelector(this.dataset.target || '.ag-prose');
    const list = this.querySelector('.ag-toc__list');
    if (!body || !list) return;

    const headings = Array.from(body.querySelectorAll('h2')).filter((h) => h.textContent.trim());
    const min = parseInt(this.dataset.min, 10) || 3;
    if (headings.length < min) return;

    const used = new Set();
    headings.forEach((heading, index) => {
      const text = heading.textContent.trim();
      if (!heading.id || used.has(heading.id)) heading.id = this.idFor(text, index, used);
      used.add(heading.id);

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = `${index + 1}. ${text}`;
      list.appendChild(link);
    });

    this.hidden = false;
  }

  /** A readable id, falling back to the position when the heading has no ASCII letters. */
  idFor(text, index, used) {
    const slug = text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48);
    const base = slug || `section-${index + 1}`;
    let id = base;
    for (let n = 2; used.has(id) || document.getElementById(id); n += 1) {
      id = `${base}-${n}`;
      if (n > 50) break;
    }
    return id;
  }
}

if (!customElements.get('ag-toc')) customElements.define('ag-toc', AgToc);

/**
 * Product cards inside migrated articles.
 *
 * Articles written in the Bloggle app embedded product cards whose image,
 * title and price were empty in the HTML and filled by that app's script
 * from its own CDN. The migration keeps the card and its product handle
 * (`a.ag-shopcard[data-ag-shopcard]`); this fills it from Shopify's own
 * /products/<handle>.js, so nothing third-party is involved and no price is
 * baked into the article body, where it would go stale on the next sale.
 *
 * The link and its label are already in the markup, so a card that never
 * fetches stays a working link rather than an empty box.
 */
(() => {
  const SEL = 'a.ag-shopcard[data-ag-shopcard]';
  const cache = new Map();
  let money = (cents) => (cents / 100).toFixed(2);

  const fetchProduct = (handle) => {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      );
    }
    return cache.get(handle);
  };

  // Shopify serves any width off the same file, so ask for what the card shows.
  const sized = (url, width) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const fill = (card, product) => {
    if (!product) return;
    const img = product.featured_image || (product.images && product.images[0]);
    const frag = document.createDocumentFragment();

    if (img) {
      const el = document.createElement('img');
      el.className = 'ag-shopcard__img';
      el.src = sized(img, 400);
      el.srcset = `${sized(img, 300)} 300w, ${sized(img, 400)} 400w, ${sized(img, 600)} 600w`;
      el.sizes = '(max-width: 700px) 45vw, 220px';
      el.alt = product.title || '';
      el.loading = 'lazy';
      el.decoding = 'async';
      frag.appendChild(el);
    }

    const title = document.createElement('span');
    title.className = 'ag-shopcard__title';
    title.textContent = product.title || '';
    frag.appendChild(title);

    if (typeof product.price === 'number') {
      const price = document.createElement('span');
      price.className = 'ag-shopcard__price';
      price.textContent = money(product.price);
      if (product.compare_at_price && product.compare_at_price > product.price) {
        const was = document.createElement('s');
        was.className = 'ag-shopcard__was';
        was.textContent = money(product.compare_at_price);
        price.appendChild(document.createTextNode(' '));
        price.appendChild(was);
      }
      frag.appendChild(price);
    }

    card.textContent = '';
    card.appendChild(frag);
    card.classList.add('is-loaded');
  };

  const hydrate = (card) => {
    const handle = card.dataset.agShopcard;
    if (!handle || card.dataset.agDone) return;
    card.dataset.agDone = '1';
    fetchProduct(handle).then((p) => fill(card, p));
  };

  const start = () => {
    const cards = document.querySelectorAll(SEL);
    if (!cards.length) return;

    const body = document.getElementById('ag-article-body');
    const code = body && body.dataset.agCurrency;
    if (code && window.Intl && Intl.NumberFormat) {
      try {
        const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: code,
        });
        money = (cents) => fmt.format(cents / 100);
      } catch (e) {
        /* the plain formatter stays */
      }
    }

    // An article can hold dozens of cards, so only fetch what comes into view.
    if (!('IntersectionObserver' in window)) {
      cards.forEach(hydrate);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          hydrate(e.target);
        });
      },
      { rootMargin: '400px 0px' }
    );
    cards.forEach((c) => io.observe(c));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
